import { useEffect, useState, useMemo } from "react";
import { COLORS } from "../../../theme/colors";

// Phase 1.5 #4 · AnimatedSpanBar
// Renders ONE span as a horizontal bar:
//   - status === "running" → animated pulse + width grows with elapsed time
//   - status === "OK"       → solid accent, snapped to final duration
//   - status === "ERROR"    → solid red, snapped to final duration
//
// Width-scale: callers pass `maxDurationMs` so all bars in a panel share
// the same horizontal scale. Running spans cap their visual width at 95%
// of `maxDurationMs` so a stuck span doesn't blow out the layout (the
// duration label still reads true elapsed).
//
// Reduced motion: respects prefers-reduced-motion — no pulse animation,
// no growing-width tween; the bar appears at current width and updates
// at the parent's 10Hz tick rate but without CSS transitions.

const PULSE_KEYFRAMES = `
@keyframes i2-span-pulse {
  0%   { opacity: 1; }
  50%  { opacity: 0.55; }
  100% { opacity: 1; }
}
`;

// Inject once per page-load. Idempotent: keyed on a stable id so React's
// dev double-mount doesn't duplicate the rule.
function ensurePulseStyle() {
  if (typeof document === "undefined") return;
  if (document.getElementById("i2-span-pulse-style")) return;
  const el = document.createElement("style");
  el.id = "i2-span-pulse-style";
  el.textContent = PULSE_KEYFRAMES;
  document.head.appendChild(el);
}

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mq.matches);
    const handler = (e) => setReduced(e.matches);
    mq.addEventListener?.("change", handler);
    return () => mq.removeEventListener?.("change", handler);
  }, []);
  return reduced;
}

export default function AnimatedSpanBar({
  name,
  status = "running",        // running | OK | ERROR | UNSET
  startedAtMs,               // epoch ms when span began
  durationMs = null,         // set after span.end arrives
  maxDurationMs = 5000,      // shared scale across the panel
  nowMs,                     // tick from parent (10Hz)
}) {
  useEffect(() => { ensurePulseStyle(); }, []);
  const reducedMotion = usePrefersReducedMotion();

  const isRunning = status === "running";
  const isError = status === "ERROR";

  // Elapsed: live for running spans, final for completed ones
  const elapsedMs = isRunning
    ? Math.max(0, (nowMs ?? Date.now()) - (startedAtMs ?? Date.now()))
    : (durationMs ?? 0);

  // Visual width: cap at 95% for running (so a stuck span stays bounded);
  // completed spans hit their true ratio.
  const ratio = useMemo(() => {
    const denom = Math.max(1, maxDurationMs);
    const raw = elapsedMs / denom;
    if (isRunning) return Math.min(0.95, Math.max(0.02, raw));
    return Math.min(1.0, Math.max(0.02, raw));
  }, [elapsedMs, maxDurationMs, isRunning]);

  const bg = isRunning
    ? (COLORS.accent || "#7c3aed")
    : isError
    ? "#dc2626"
    : "#10b981";

  return (
    <div
      data-testid="animated-span-bar"
      data-status={status}
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) 60px",
        gap: 8,
        alignItems: "center",
        padding: "3px 0",
        fontSize: 11,
      }}
    >
      <div
        style={{
          position: "relative",
          height: 10,
          background: COLORS.surface,
          border: `1px solid ${COLORS.border}`,
          borderRadius: 4,
          overflow: "hidden",
        }}
        title={name}
      >
        <div
          style={{
            position: "absolute",
            left: 0, top: 0, bottom: 0,
            width: `${Math.round(ratio * 100)}%`,
            background: bg,
            transition: reducedMotion ? "none" : "width 100ms linear",
            animation: isRunning && !reducedMotion
              ? "i2-span-pulse 1.4s ease-in-out infinite"
              : "none",
          }}
        />
        <div
          style={{
            position: "absolute",
            left: 8, top: -1,
            color: COLORS.text,
            fontSize: 10,
            fontWeight: 600,
            mixBlendMode: "difference",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: "calc(100% - 16px)",
            lineHeight: "12px",
          }}
        >
          {name}
        </div>
      </div>
      <div
        style={{
          fontFamily: "monospace",
          fontSize: 10,
          color: COLORS.textMuted,
          textAlign: "right",
        }}
      >
        {elapsedMs >= 1000
          ? `${(elapsedMs / 1000).toFixed(1)}s`
          : `${Math.round(elapsedMs)}ms`}
      </div>
    </div>
  );
}
